/* Row Reloader (Rehydrator)
  Purpose: Look at the currently selected row in Excel and rebuild the user's selection
  of practices and sites from the sheet values.
 This will enable row editing: loading an existing row's practices/sites back into the UI
 for further modification.
  High-level steps:
  1. Find the active row (top row of current selection).
  2. Read the four columns we care about (Practice Names, Site Names, Practice IDs, Site IDs). (Practice Ids and Site Ids are the source of truth and are hidden in the sheet)
  3. If IDs are present, use them directly (fast + reliable).
  4. If IDs are missing but names exist, try to convert names into IDs using the cached hierarchy.
  5. Return arrays of practiceIds and siteIds plus a message and the source row number.

  Notes:
  - Columns indices come from writerService.COLUMN (G–J in the sheet layout).
  - Name->ID lookup only runs when ID cells are empty to avoid accidental overrides.
  - Returned row number is 1-based for user-friendly display.
*/
/* global Excel */
import { Constants } from "../constants/constants";
// Turn any cell value into a trimmed string; null/undefined -> empty string.
function sanitizeCellValue(v) {
    if (v == null)
        return "";
    return String(v).trim();
}
export async function rehydrateFromActiveRow() {
    return Excel.run(async (context) => {
        // 1. Identify the worksheet and the top row of the current selection.
        const sheet = context.workbook.worksheets.getActiveWorksheet();
        const sel = context.workbook.getSelectedRange();
        sel.load(["rowIndex"]);
        await context.sync();
        const row = sel.rowIndex; // 0-based index from Excel API
        // 2. Grab the contiguous range covering the 4 columns (Practice Name, Site Name, Practice ID, Site ID).
        //    We request exactly 1 row (the active row) and the needed width.
        const range = sheet.getRangeByIndexes(row, Constants.SITE_PRACTICE_COLUMNS.PRACTICE_NAME, 1, Constants.SITE_PRACTICE_COLUMNS.SITE_ID - Constants.SITE_PRACTICE_COLUMNS.PRACTICE_NAME + 1);
        range.load("values");
        // Also read role columns (F for name, K for ID)
        const roleNameCell = sheet.getCell(row, Constants.ROLE_COLUMNS.ROLE_NAME);
        const roleIdCell = sheet.getCell(row, Constants.ROLE_COLUMNS.ROLE_ID);
        roleNameCell.load("values");
        roleIdCell.load("values");
        await context.sync();
        // values[0] is the array of the four cell values in order.
        const values = range.values?.[0] || [];
        // 3. Sanitize raw cell strings.
        const practiceNamesRaw = sanitizeCellValue(values[0]);
        const siteNamesRaw = sanitizeCellValue(values[1]);
        const practiceIdsRaw = sanitizeCellValue(values[2]);
        const siteIdsRaw = sanitizeCellValue(values[3]);
        // Cached hierarchy for name->ID lookup if needed.
        const hierarchy = window.__practiceHierarchy;
        // Role values
        const roleNamesRaw = sanitizeCellValue(roleNameCell.values?.[0]?.[0]);
        const roleIdsRaw = sanitizeCellValue(roleIdCell.values?.[0]?.[0]);
        // 4. If ID cells contain comma-separated lists, split + clean them.
        const practiceIds = practiceIdsRaw
            ? practiceIdsRaw
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            : [];
        const siteIds = siteIdsRaw
            ? siteIdsRaw
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            : [];
        // 5. If IDs are missing, derive them by exact name matching in the hierarchy.
        if (!practiceIds.length && practiceNamesRaw && hierarchy) {
            const nameParts = practiceNamesRaw
                .split(/[,;]/) // Support comma or semicolon separators
                .map((p) => p.trim())
                .filter(Boolean);
            nameParts.forEach((nm) => {
                const match = hierarchy.practices.find((p) => p.name === nm);
                if (match && !practiceIds.includes(match.id))
                    practiceIds.push(match.id);
            });
        }
        if (!siteIds.length && siteNamesRaw && hierarchy) {
            const nameParts = siteNamesRaw
                .split(/[,;]/)
                .map((p) => p.trim())
                .filter(Boolean);
            nameParts.forEach((nm) => {
                const match = hierarchy.practices.flatMap((p) => p.sites).find((s) => s.name === nm);
                if (match && !siteIds.includes(match.id))
                    siteIds.push(match.id);
            });
        }
        // 6. Rehydrate role IDs
        const roleIds = roleIdsRaw
            ? roleIdsRaw
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            : [];
        // If role IDs are missing but names exist, derive IDs from cached roles list
        if (!roleIds.length && roleNamesRaw) {
            const rolesList = window.__rolesList;
            if (rolesList) {
                const nameParts = roleNamesRaw
                    .split(/[,;]/)
                    .map((p) => p.trim())
                    .filter(Boolean);
                nameParts.forEach((nm) => {
                    const match = rolesList.byRoleName[nm.toLowerCase()];
                    if (match && !roleIds.includes(match.id))
                        roleIds.push(match.id);
                });
            }
        }
        // 7. Return the reconstructed selection. Convert row to 1-based for user messaging.
        return {
            practiceIds,
            siteIds,
            roleIds,
            sourceRow: row + 1,
            message: `Rehydrated from row ${row + 1}`,
        };
    });
}
